import { ToolSchema } from '../types';

export const portfolioToolSchemas: ToolSchema[] = [
  {
    name: 'get_portfolio_balances',
    description:
      'Get the current portfolio balances including current holdings value, capital invested, profit and loss, net position, inflows, outflows, and management fees. Use this to answer questions about portfolio performance and financial summary.',
    parameters: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Optional portfolio ID to get balances for a specific portfolio',
        },
      },
    },
  },
  {
    name: 'get_portfolio_performance',
    description:
      'Get the historical portfolio performance data as a time series showing current holdings value and net contributions over time. Use this to show trends and performance history.',
    parameters: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Optional portfolio ID to get performance for a specific portfolio',
        },
        period: {
          type: 'string',
          description: 'Time period for performance data',
          enum: ['1M', '3M', '6M', '1Y', 'ALL'],
        },
      },
    },
  },
  {
    name: 'get_regional_allocation',
    description:
      'Get the current regional allocation breakdown showing percentage allocation to each wine region (Bordeaux, Burgundy, Champagne, etc.) along with current holdings, purchase price, and P&L per region.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_current_holdings',
    description:
      'Get the list of current wine holdings in the portfolio. Returns wine names, vintages, quantities, values, costs, and profit/loss. Use sortBy and limit to get top performers or specific views.',
    parameters: {
      type: 'object',
      properties: {
        sortBy: {
          type: 'string',
          description: 'Field to sort by',
          enum: ['profitAndLoss', 'totalValue', 'profitAndLossPct', 'vintage', 'wineName'],
        },
        sortDirection: {
          type: 'string',
          description: 'Sort direction',
          enum: ['asc', 'desc'],
        },
        limit: {
          type: 'number',
          description: 'Maximum number of holdings to return (default 10)',
        },
      },
    },
  },
  {
    name: 'get_cellar_stats',
    description:
      'Get summary statistics about the wine cellar including total bottles, total cases, total value, and number of unique wines.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_cellar_insights',
    description:
      'Get actionable insights about the wine cellar including wines at/approaching/past peak maturity, wines with active bids, strong performers, and portfolio concentration warnings.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_sold_holdings',
    description:
      'Get the list of wines that have been sold from the portfolio. Returns wine names, vintages, sold values, cost basis, and realized profit/loss. Use this to answer questions about sale history and realized gains.',
    parameters: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description: 'Maximum number of sold holdings to return (default 20)',
        },
      },
    },
  },
  {
    name: 'get_external_holdings',
    description:
      'Get the list of wines held in external storage (not in Cult Wines managed storage). Returns wine names, vintages, values, and storage locations.',
    parameters: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description: 'Maximum number of external holdings to return (default 20)',
        },
      },
    },
  },
  {
    name: 'get_management_fees',
    description:
      'Get the management fees charged to the account including fee amounts, types, invoice dates, and payment status. Use this to answer questions about fees, charges, and billing.',
    parameters: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description: 'Maximum number of fee records to return (default 20)',
        },
      },
    },
  },
  {
    name: 'get_user_profile',
    description:
      'Get the user profile information including name, email, address, and account status. Use this to answer questions about account details and personal information.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_user_preferences',
    description:
      'Get the user account preferences including currency, language, auto-trade settings, and fee payment preferences. Use this to answer questions about account settings.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_detailed_holdings',
    description:
      'Get detailed current wine holdings with comprehensive market data including current market prices, price history (1/3/6/12 months), wine ratings (Wine Advocate, Vinous), active bids/offers, trading information, storage location, and performance metrics. Use this for detailed wine analysis, market trends, and investment performance questions.',
    parameters: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'number',
          description: 'Portfolio ID to get holdings for. Use -1 for all portfolios (default).',
        },
        sortBy: {
          type: 'string',
          description: 'Field to sort by',
          enum: [
            'totalValue',
            'profitAndLoss',
            'profitAndLossPct',
            'marketPrice',
            'changePercent1Month',
            'changePercent12Months',
            'vintage',
            'wineName',
          ],
        },
        sortDirection: {
          type: 'string',
          description: 'Sort direction',
          enum: ['asc', 'desc'],
        },
        limit: {
          type: 'number',
          description: 'Maximum number of holdings to return (default 10)',
        },
        includeMarketData: {
          type: 'boolean',
          description: 'Include market prices, price history, ratings, and trading data (default true)',
        },
      },
    },
  },
  {
    name: 'get_return_valuations',
    description:
      'Get historical return valuations for a portfolio showing time-series valuation data including valuation start/end, purchases, sales, inflows, outflows, net capital invested, net position, and monthly holding returns. Use this to analyze portfolio performance over time, understand capital movements, and track investment returns. If the user has multiple portfolios and no portfolio is specified, this will return available portfolio options for the user to choose from.',
    parameters: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description:
            'Portfolio ID to get valuations for. Use "all" for all portfolios combined. If not specified and user has multiple portfolios, available options will be returned.',
        },
        valuationDate: {
          type: 'string',
          description: 'Specific valuation date in YYYY-MM-DD format (optional, returns all dates if not specified)',
        },
        page: {
          type: 'number',
          description: 'Page number for pagination (default 0)',
        },
        pageSize: {
          type: 'number',
          description: 'Number of records per page (default 20)',
        },
      },
    },
  },
];
